import { useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import { cn } from "@/utils/cn";
import { useImportFileUpload } from "@/hooks/useImportFileUpload";
import type { ImportUploadSnapshot } from "@/hooks/useImportFileUpload";

// Import popover content — file drop zone + paste textarea.
//
// File upload happens on file pick/drop (NOT on submit) so the user sees
// progress while bytes travel to S3. Submit is gated on the upload reaching
// the `uploaded` status — clicking it only kicks off the parse step.
//
// The drop zone has real DnD handlers; the previous implementation only had
// a styled <label>, so drops were silently ignored.
type ImportFormProps = {
  onClose: () => void;
  onSubmit: (args: { text?: string; fileUploadId?: string }) => Promise<void>;
  isPending: boolean;
  isError: boolean;
  flavorFormulaId: number | null;
};

export function ImportForm({
  onClose,
  onSubmit,
  isPending,
  isError,
  flavorFormulaId,
}: ImportFormProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Controlled textarea — the previous uncontrolled version meant
  // textRef.current.value was a snapshot at render time and never triggered
  // a re-render, so the Submit button stayed disabled even when the user
  // had typed content.
  const [pastedText, setPastedText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const upload = useImportFileUpload();
  const snapshot = upload.snapshot;

  // True while bytes are flowing or the presign request is in flight.
  const uploading = snapshot?.status === "presigning" || snapshot?.status === "uploading";
  const uploadFailed = snapshot?.status === "failed";
  const uploadDone = snapshot?.status === "uploaded";

  const handleFile = (file: File) => {
    if (!flavorFormulaId) return;
    upload.start(file, flavorFormulaId);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) handleFile(file);
    e.target.value = ""; // allow picking the same file twice
  };

  const onDrop: React.DragEventHandler<HTMLElement> = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleSubmit = () => {
    if (snapshot && uploadDone && snapshot.fileUploadId) {
      void onSubmit({ fileUploadId: snapshot.fileUploadId });
    } else if (pastedText.trim().length > 0) {
      void onSubmit({ text: pastedText });
    }
  };

  // Submit is allowed when: parse isn't already running AND either a file
  // upload completed OR there's pasted text to send. The pasted-text branch
  // works WITHOUT requiring a file (the original ask: text-only Submit).
  const canSubmit =
    !isPending && !uploading && (uploadDone || pastedText.trim().length > 0);

  return (
    <div className="import-form">
      <span className="import-form-help">
        Drop a file or paste a list of compounds. We'll parse and fill the flask.
      </span>

      {snapshot ? (
        <div className={cn("import-upload-row", snapshot.status)}>
          <div className="import-upload-icon">
            <Icon name="file" size={14} />
          </div>
          <div className="import-upload-body">
            <div className="import-upload-line">
              <span className="import-upload-filename" title={snapshot.filename}>
                {snapshot.filename}
              </span>
            </div>
            <UploadStatusText snapshot={snapshot} />
            {snapshot.status === "uploading" && (
              <div className="import-upload-bar">
                <div
                  className="import-upload-bar-fill"
                  style={{ width: `${snapshot.progressPct}%` }}
                />
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn xs ghost"
            onClick={() => upload.reset()}
            title={uploading ? "Cancel upload" : "Remove file"}
          >
            <Icon name={uploading ? "x" : "trash"} size={11} />
          </button>
        </div>
      ) : (
        <label
          htmlFor="flask-import-file"
          className={cn("import-drop-zone", dragOver && "drag-over")}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <Icon name="upload" size={16} />
          <span className="import-drop-headline">Drop files or click to upload</span>
          <span className="mono import-drop-formats">CSV · TSV · XLSX · JSON · PDF</span>
        </label>
      )}
      <input
        id="flask-import-file"
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.xlsx,.xls,.json,.txt,.pdf,.png,.jpg,.jpeg,.gif,.webp"
        style={{ display: "none" }}
        onChange={onFileChange}
      />

      <div className="vstack" style={{ gap: 4 }}>
        <span className="import-form-section-label mono">Or paste</span>
        <textarea
          rows={4}
          className="mono import-form-textarea"
          disabled={isPending || uploading}
          placeholder={"Furaneol, 38 ppm\nγ-Decalactone, 18 ppm\nVanillin, 12 ppm"}
          value={pastedText}
          onChange={(e) => setPastedText(e.target.value)}
        />
      </div>

      {isError && (
        <div className="import-form-error">
          Parse failed — check your input and try again.
        </div>
      )}

      <div className="hstack" style={{ justifyContent: "flex-end", gap: 6 }}>
        <button
          type="button"
          className="btn xs ghost"
          onClick={onClose}
          disabled={isPending || uploading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn xs primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          title={
            uploading
              ? "Wait for the upload to finish"
              : uploadFailed
              ? "Upload failed — remove the file and try again"
              : undefined
          }
        >
          <Icon name="check" size={11} />
          {isPending ? "Parsing…" : uploading ? "Uploading…" : "Submit"}
        </button>
      </div>
    </div>
  );
}

function UploadStatusText({ snapshot }: { snapshot: ImportUploadSnapshot }) {
  const size = formatFileSize(snapshot.sizeBytes);
  switch (snapshot.status) {
    case "presigning":
      return <span className="import-upload-status">Preparing upload · {size}</span>;
    case "uploading":
      return (
        <span className="import-upload-status">
          Uploading {snapshot.progressPct}% · {size}
        </span>
      );
    case "uploaded":
      return (
        <span className="import-upload-status done">
          <Icon name="check" size={10} /> Uploaded · {size}
        </span>
      );
    case "failed":
      return (
        <span className="import-upload-status fail">
          Failed: {snapshot.errorMessage ?? "unknown error"}
        </span>
      );
    case "idle":
      return <span className="import-upload-status">{size}</span>;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
